import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarDays, Loader2, Shuffle, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const EMPTY = { code: '', type: 'percent', value: '', store_ids: [], starts_at: '', ends_at: '', max_uses: '', min_amount: '', once_per_email: false, auto_apply: false, active: true };

const randomCode = () => Array.from({ length: 8 }, () => 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)]).join('');

const DateField = ({ value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const d = value ? parseISO(value.slice(0, 10)) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid={testid} className="w-full justify-start font-normal">
            <CalendarDays className="h-4 w-4 mr-2 text-zinc-400" />
            {d ? format(d, 'd MMMM yyyy', { locale: fr }) : <span className="text-zinc-400">Aucune</span>}
          </Button>
        </PopoverTrigger>
        {d && (
          <button type="button" onClick={() => onChange('')} data-testid={`${testid}-clear`} aria-label="Effacer"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-400 hover:text-zinc-700"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
      <PopoverContent className="w-auto min-w-[290px] p-0" align="start">
        <Calendar mode="single" locale={fr} selected={d} defaultMonth={d}
          onSelect={(v) => { onChange(v ? format(v, 'yyyy-MM-dd') : ''); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
};

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-zinc-600">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-zinc-400">{hint}</p>}
  </div>
);

export const PromoForm = ({ open, onClose, editing, stores, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setF(editing ? { ...EMPTY, ...editing, max_uses: editing.max_uses || '', min_amount: editing.min_amount || '' } : EMPTY);
  }, [open, editing]);

  const toggleStore = (id) => set('store_ids', f.store_ids.includes(id) ? f.store_ids.filter((s) => s !== id) : [...f.store_ids, id]);

  const save = async () => {
    setSaving(true);
    const body = {
      code: f.code.trim(), type: f.type, value: Number(f.value), store_ids: f.store_ids,
      starts_at: f.starts_at || '', ends_at: f.ends_at || '', max_uses: Number(f.max_uses || 0),
      min_amount: Number(f.min_amount || 0), once_per_email: f.once_per_email, auto_apply: !!f.auto_apply, active: f.active,
    };
    try {
      if (editing) await api.put(`/promo-codes/${editing.id}`, body);
      else await api.post('/promo-codes', body);
      toast.success(editing ? 'Code mis à jour' : 'Code créé');
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Vérifie les champs (code, valeur, au moins une boutique)');
    } finally { setSaving(false); }
  };

  const valid = f.code.trim().length >= 2 && Number(f.value) > 0 && f.store_ids.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="promo-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Modifier le code promo' : 'Nouveau code promo'}</DialogTitle>
          <DialogDescription>Le client saisit ce code dans le checkout, ou il est appliqué automatiquement ; la réduction s'applique avant le paiement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <Field label="Code">
            <div className="flex gap-2">
              <Input value={f.code} onChange={(e) => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} placeholder="BIENVENUE10" data-testid="promo-form-code" className="font-mono tracking-wider" />
              <Button type="button" variant="outline" onClick={() => set('code', randomCode())} data-testid="promo-form-random"><Shuffle className="h-4 w-4" /></Button>
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Type de réduction">
              <div className="grid grid-cols-2 rounded-md border border-zinc-200 p-1 text-sm">
                {[['percent', '%'], ['fixed', 'Montant']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => set('type', k)} data-testid={`promo-form-type-${k}`}
                    className={`py-1.5 rounded transition-colors ${f.type === k ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}>{l}</button>
                ))}
              </div>
            </Field>
            <Field label={f.type === 'percent' ? 'Pourcentage (%)' : 'Montant (devise de la boutique)'}>
              <Input type="number" min="0" step="0.01" value={f.value} onChange={(e) => set('value', e.target.value)} placeholder={f.type === 'percent' ? '10' : '5'} data-testid="promo-form-value" />
            </Field>
          </div>
          <Field label="Boutiques concernées">
            <div className="flex flex-wrap gap-2" data-testid="promo-form-stores">
              {stores.map((s) => {
                const on = f.store_ids.includes(s.id);
                return (
                  <button key={s.id} type="button" onClick={() => toggleStore(s.id)} data-testid={`promo-form-store-${s.id}`}
                    className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${on ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-600 hover:border-zinc-500'}`}>{s.name}</button>
                );
              })}
              {stores.length === 0 && <p className="text-xs text-zinc-400">Aucune boutique.</p>}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Début (optionnel)"><DateField value={f.starts_at} onChange={(v) => set('starts_at', v)} testid="promo-form-start" /></Field>
            <Field label="Fin (optionnel)"><DateField value={f.ends_at} onChange={(v) => set('ends_at', v)} testid="promo-form-end" /></Field>
            <Field label="Utilisations max" hint="Vide = illimité"><Input type="number" min="0" value={f.max_uses} onChange={(e) => set('max_uses', e.target.value)} placeholder="∞" data-testid="promo-form-max-uses" /></Field>
            <Field label="Panier minimum" hint="Hors livraison, vide = aucun"><Input type="number" min="0" step="0.01" value={f.min_amount} onChange={(e) => set('min_amount', e.target.value)} placeholder="0" data-testid="promo-form-min-amount" /></Field>
          </div>
          <Field label="Mode d'application">
            <div className="grid grid-cols-2 rounded-md border border-zinc-200 p-1 text-sm" data-testid="promo-form-mode">
              {[[false, 'Saisie manuelle', 'Le client tape le code au checkout'], [true, 'Automatique', 'Réduction appliquée dès l\'ouverture du checkout']].map(([v, l, d]) => (
                <button key={String(v)} type="button" onClick={() => set('auto_apply', v)} data-testid={`promo-form-mode-${v ? 'auto' : 'manual'}`}
                  className={`py-2 px-2 rounded text-left transition-colors ${!!f.auto_apply === v ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}>
                  <div className="font-medium">{l}</div>
                  <div className={`text-[11px] ${!!f.auto_apply === v ? 'text-zinc-300' : 'text-zinc-400'}`}>{d}</div>
                </button>
              ))}
            </div>
            {f.auto_apply && <p className="text-[11px] text-amber-700">Un seul code automatique actif par boutique. Le client voit la réduction déjà appliquée et peut la retirer pour saisir un autre code.</p>}
          </Field>
          <div className="space-y-3 rounded-lg border border-zinc-200 p-3">
            <label className="flex items-center justify-between text-sm">Une seule utilisation par client (email)
              <Switch checked={f.once_per_email} onCheckedChange={(v) => set('once_per_email', v)} data-testid="promo-form-once" /></label>
            <label className="flex items-center justify-between text-sm">Code actif
              <Switch checked={f.active} onCheckedChange={(v) => set('active', v)} data-testid="promo-form-active" /></label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="promo-form-cancel">Annuler</Button>
          <Button onClick={save} disabled={!valid || saving} data-testid="promo-form-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing ? 'Enregistrer' : 'Créer le code'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
